import { useCallback, useRef, useState } from 'react';

/**
 * Hosts the confirmation lifecycle for an inline cost-of-record change gate so
 * any page that edits a cost-moving field resolves the gate WHERE the edit
 * happens — no punt to another screen.
 *
 * The backend returns HTTP 200 `{ requires_confirmation, change_context,
 * affected_items }` when a save would move an item's cost-of-record (a
 * default-supplier adopt, or a supplier-currency reinterpret). The caller
 * hands that payload to `request`; the merchant confirms in
 * <CurrencyChangeConfirmModal>; the caller's `onConfirm` re-issues the SAME
 * save, spreading the modal's confirmation body fragment into the request —
 * `{ confirm_cost_change: true }` for an adopt, `{ currency_change_strategy }`
 * for a reinterpret. The modal decides which; the caller stays trigger-agnostic.
 *
 * Deep module: the whole modal state machine (open / loading / in-modal error /
 * confirm / cancel) lives here. Callers see three things — `request` to open
 * the gate (awaitable), `modalProps` to spread onto the modal, and the boolean
 * the request resolves to (true = confirmed + succeeded, false = cancelled).
 *
 * @returns {{
 *   request: (opts: {
 *     changeContext: object,
 *     affectedItems: object[],
 *     onConfirm: (confirmBody: object) => Promise<{ error?: string } | void>,
 *   }) => Promise<boolean>,
 *   modalProps: object,
 *   isOpen: boolean,
 * }}
 */
export function useResolutionGate() {
  const [open, setOpen] = useState(false);
  const [changeContext, setChangeContext] = useState(null);
  const [affectedItems, setAffectedItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const onConfirmRef = useRef(null);
  const resolveRef = useRef(null);

  const settle = useCallback((result) => {
    setOpen(false);
    setLoading(false);
    setError(null);
    onConfirmRef.current = null;
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(result);
  }, []);

  // Open the gate. Resolves true once the merchant confirms AND `onConfirm`
  // reports success; false if they cancel. `onConfirm(strategy)` performs the
  // caller's retry while the modal shows a spinner; return `{ error }` to keep
  // the modal open with a message, or nothing to close on success.
  const request = useCallback(({ changeContext, affectedItems, onConfirm }) => {
    // The payload is the prompt (utils/writeGates.js): a gate body without its
    // rows is a defect to report, never a question to ask with a blank list.
    if (!Array.isArray(affectedItems)) {
      throw new Error('cost gate opened without affected_items');
    }
    onConfirmRef.current = onConfirm;
    setChangeContext(changeContext);
    setAffectedItems(affectedItems);
    setError(null);
    setLoading(false);
    setOpen(true);
    return new Promise((resolve) => { resolveRef.current = resolve; });
  }, []);

  const handleConfirm = useCallback(async (confirmBody) => {
    setLoading(true);
    setError(null);
    // `onConfirm` owns its retry and reports failure by returning a
    // translated `{ error }` — it must not throw. The `finally` clears the
    // spinner even if it does (a caller-contract violation), and that throw
    // then propagates loudly rather than being masked as an untranslated
    // banner message. No user-facing string is ever fabricated here.
    let result;
    try {
      result = await onConfirmRef.current?.(confirmBody);
    } finally {
      setLoading(false);
    }
    if (result?.error) {
      setError(result.error);
      return;
    }
    settle(true);
  }, [settle]);

  const handleCancel = useCallback(() => settle(false), [settle]);

  return {
    request,
    isOpen: open,
    modalProps: {
      open,
      changeContext,
      affectedItems,
      loading,
      errorMessage: error,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
}
